<template>
  <div class="pb-page im">
    <router-link to="/testimonials" class="pb-button link pb-back im-back"><i class="mdi mdi-arrow-left" aria-hidden="true"></i> Testimonials</router-link>
    <PageHeader title="Import existing testimonials" :subtitle="'Bring in praise you already have for ' + withStop(brand ? brand.name : 'your brand') + ' You note where it came from and confirm you may publish it.'" />

    <section v-if="result" class="pb-card im-done" aria-live="polite">
      <span class="pb-empty-icon" aria-hidden="true"><i class="mdi mdi-check-all"></i></span>
      <h2 class="pb-section-title display-font">{{ resultTitle }}</h2>
      <p class="pb-body">{{ resultText }}</p>
      <div class="pb-actions im-done-actions">
        <template v-if="result.status === 'approved' && !publishedNow">
          <LockedAction v-if="publishCap" feature="published" look="pb-button" label="Publish all" :cap="publishCap" :used="usage.published || 0" />
          <button v-else type="button" class="pb-button" :disabled="busy" @click="publishAll">{{ busy ? 'Publishing…' : 'Publish all' }}</button>
        </template>
        <router-link v-if="publishedNow && isOwner" :to="{ path: '/subscribe', query: { plan: upgradeTarget } }" class="pb-button">See {{ planName(upgradeTarget) }}</router-link>
        <router-link to="/testimonials" class="pb-button outline">{{ publishedNow ? 'Choose what stays published' : 'Choose what to publish' }}</router-link>
        <button type="button" class="pb-button ghost" @click="reset">Import more</button>
      </div>
    </section>

    <template v-else>
      <div class="pb-segmented im-tabs" role="tablist" aria-label="How to import">
        <button v-for="tab in tabs" :key="tab.key" type="button" role="tab" :aria-selected="mode === tab.key ? 'true' : 'false'" @click="mode = tab.key">{{ tab.label }}</button>
      </div>

      <section v-show="mode === 'paste'" class="pb-card" aria-labelledby="im-paste">
        <h2 id="im-paste" class="pb-section-title display-font im-h">Paste</h2>
        <label class="pb-label" for="im-paste-text">Paste one testimonial per paragraph</label>
        <textarea id="im-paste-text" v-model="pasteText" class="pb-textarea im-paste" rows="10" :placeholder="pastePlaceholder"></textarea>
        <span class="pb-help">Leave an empty line between testimonials. A last line like "- Jane Doe, Acme" becomes the name and company.</span>
        <div v-if="blocks.length" class="im-blocks">
          <p class="pb-label">{{ blocks.length }} testimonial{{ blocks.length === 1 ? '' : 's' }} found</p>
          <div v-for="(block, index) in blocks" :key="index" class="im-block">
            <p class="im-block-text">{{ block.text }}</p>
            <div class="im-block-fields">
              <label class="pb-sr-only" :for="'im-bn-' + index">Name for testimonial {{ index + 1 }}</label>
              <input :id="'im-bn-' + index" v-model="block.name" class="pb-input" maxlength="120" placeholder="Name">
              <label class="pb-sr-only" :for="'im-bc-' + index">Company for testimonial {{ index + 1 }}</label>
              <input :id="'im-bc-' + index" v-model="block.company" class="pb-input" maxlength="120" placeholder="Company">
            </div>
          </div>
        </div>
      </section>

      <section v-show="mode === 'csv'" class="pb-card" aria-labelledby="im-csv">
        <h2 id="im-csv" class="pb-section-title display-font im-h">Upload a CSV</h2>
        <label class="pb-file">
          <input type="file" accept=".csv,text/csv" @change="pickCsv">
          <span class="pb-button outline"><i class="mdi mdi-file-upload-outline pb-ico" aria-hidden="true"></i>Choose a CSV file</span>
          <span class="pb-muted">{{ csvName ? csvName + ' · ' + plural(table.length - (hasHeader ? 1 : 0), 'testimonial') : 'Up to 500 rows at a time. Exported from a spreadsheet, a reviews site or a form tool.' }}</span>
        </label>
        <div v-if="csvError" class="pb-alert is-error" role="alert"><span>{{ csvError }}</span></div>
        <template v-if="table.length">
          <label class="pb-check im-header"><input v-model="hasHeader" type="checkbox">The first row holds column names</label>
          <div class="im-map">
            <div v-for="(label, field) in fieldLabels" :key="field" class="im-map-row">
              <label class="pb-label" :for="'im-map-' + field">{{ label }}<span v-if="field === 'text'" class="optional"> (required)</span></label>
              <select :id="'im-map-' + field" v-model.number="mapping[field]" class="pb-select">
                <option :value="-1">Not in this file</option>
                <option v-for="(header, index) in headers" :key="index" :value="index">{{ header }}</option>
              </select>
            </div>
          </div>
          <p class="pb-label im-preview-title">Preview of the first {{ Math.min(20, csvRows.rows.length) }} rows</p>
          <div class="im-table-wrap">
            <table class="im-table">
              <thead><tr><th scope="col">Testimonial</th><th scope="col">Name</th><th scope="col">Company</th><th scope="col">Rating</th><th scope="col">Date</th></tr></thead>
              <tbody><tr v-for="(row, index) in csvRows.rows.slice(0, 20)" :key="index">
                <td>{{ row.text }}</td><td>{{ row.name }}</td><td>{{ row.company }}</td><td>{{ row.rating }}</td><td>{{ row.date }}</td></tr></tbody>
            </table>
          </div>
          <div v-if="csvRows.problems.length" class="pb-alert" role="status"><i class="mdi mdi-alert-outline pb-ico" aria-hidden="true"></i>
            <div><strong>{{ csvRows.problems.length }} row{{ csvRows.problems.length === 1 ? '' : 's' }} will be skipped:</strong>
              <ul class="im-problems"><li v-for="problem in csvRows.problems.slice(0, 8)" :key="problem.row">Row {{ problem.row }}: {{ problem.message }}</li></ul></div></div>
        </template>
      </section>

      <section v-show="mode === 'manual'" class="pb-card" aria-labelledby="im-manual">
        <h2 id="im-manual" class="pb-section-title display-font im-h">Add one by hand</h2>
        <div class="pb-field"><label class="pb-label" for="im-m-text">Testimonial</label>
          <textarea id="im-m-text" v-model="manual.text" class="pb-textarea" rows="5" maxlength="3000"></textarea></div>
        <div class="im-two">
          <div class="pb-field"><label class="pb-label" for="im-m-name">Name <span class="optional">(optional)</span></label><input id="im-m-name" v-model="manual.name" class="pb-input" maxlength="120"></div>
          <div class="pb-field"><label class="pb-label" for="im-m-title">Job title <span class="optional">(optional)</span></label><input id="im-m-title" v-model="manual.job_title" class="pb-input" maxlength="120"></div>
          <div class="pb-field"><label class="pb-label" for="im-m-company">Company <span class="optional">(optional)</span></label><input id="im-m-company" v-model="manual.company" class="pb-input" maxlength="120"></div>
          <div class="pb-field"><label class="pb-label" for="im-m-date">Date <span class="optional">(optional)</span></label><input id="im-m-date" v-model="manual.date" class="pb-input" type="date" :max="today"></div>
        </div>
        <div class="pb-field"><StarRating v-model="manual.rating" input name="im-rating" legend="Rating (optional)" /></div>
        <div class="pb-field">
          <span class="pb-label">Photo <span class="optional">(optional)</span></span>
          <label class="pb-file"><input type="file" accept="image/png,image/jpeg,image/webp" @change="pickPhoto">
            <span class="pb-button outline sm"><i class="mdi mdi-account-box-outline pb-ico" aria-hidden="true"></i>{{ photo ? 'Change photo' : 'Add a photo' }}</span>
            <span class="pb-muted">{{ photoStatus }}</span></label>
        </div>
      </section>

      <section class="pb-card im-confirm" aria-labelledby="im-confirm-title">
        <h2 id="im-confirm-title" class="pb-sr-only">Source and permission</h2>
        <div class="pb-field"><label class="pb-label" for="im-source">Where is this from?</label>
          <input id="im-source" v-model="source" class="pb-input" maxlength="160" placeholder="e.g. Email from client, March 2026" :class="{ invalid: tried && !source.trim() }">
          <span class="pb-help">Shown as the source line with each testimonial, so visitors know where it came from.</span>
          <span v-if="tried && !source.trim()" class="pb-field-error">Say where these testimonials are from.</span></div>
        <label class="pb-check im-attest"><input v-model="attest" type="checkbox">I confirm I have permission to publish these testimonials and the details shown with them.</label>
        <span v-if="tried && !attest" class="pb-field-error">Please confirm you have permission.</span>
        <div v-if="error" class="pb-alert is-error" role="alert"><span>{{ error }}</span></div>
        <div class="pb-actions im-go">
          <button type="button" class="pb-button lg" :disabled="busy || !rowCount" @click="submit">{{ busy ? 'Importing…' : importLabel }}</button>
          <span class="pb-muted">{{ brand && brand.requires_client_approval ? 'They go to review, because this brand needs client approval.' : 'They arrive approved, ready to publish.' }}</span>
        </div>
      </section>
    </template>
    <UpgradeDialog v-model="upgradeOpen" :detail="upgrade" />
  </div>
</template>

<script>
import PageHeader from '../components/PageHeader.vue';
import StarRating from '../components/StarRating.vue';
import UpgradeDialog from '../components/UpgradeDialog.vue';
import LockedAction from '../components/LockedAction.vue';
import workspace from '../mixins/workspace';
import { post, uploadForm, message, planLimit } from '../api';
import { FIELD_LABELS, looksLikeHeader, mapHeaders, parseCsv, toImportRows } from '../lib/csv';
import { parseBlocks } from '../lib/paste';
import { plural, withStop } from '../lib/format';
import { publishedToast } from '../lib/showing';
import { toast } from '../lib/toast';

export default {
  name: 'ImportView',
  components: { LockedAction, PageHeader, StarRating, UpgradeDialog },
  mixins: [workspace],
  data: () => ({
    mode: 'paste', pasteText: '', blocks: [], table: [], hasHeader: true, mapping: {}, csvName: '', csvError: '',
    manual: { text: '', name: '', job_title: '', company: '', date: '', rating: null }, photo: null, photoStatus: 'PNG, JPG or WebP up to 8 MB.',
    source: '', attest: false, tried: false, busy: false, error: '', result: null, publishedNow: 0, upgrade: null, upgradeOpen: false,
    fieldLabels: FIELD_LABELS,
    pastePlaceholder: 'They answered every question the same day.\n- Jane Doe, Acme\n\nClear prices and no surprises.'
  }),
  computed: {
    // At the plan's cap, with a plan above that publishes more: Publish all shows the cap instead of failing.
    publishCap() { return this.capOf('published', this.usage.published || 0); },
    tabs() { return [{ key: 'paste', label: 'Paste' }, { key: 'csv', label: 'CSV' }, { key: 'manual', label: 'Manual' }]; },
    today() { return new Date().toISOString().slice(0, 10); },
    headers() {
      const first = this.table[0] || [];
      return first.map((cell, index) => (this.hasHeader ? (cell || 'Column ' + (index + 1)) : 'Column ' + (index + 1) + (cell ? ' (' + String(cell).slice(0, 24) + ')' : '')));
    },
    csvRows() { return this.table.length ? toImportRows(this.table, this.mapping, this.hasHeader) : { rows: [], problems: [] }; },
    rows() {
      if (this.mode === 'paste') return this.blocks.map(block => ({ text: block.text, name: block.name || null, company: block.company || null }));
      if (this.mode === 'csv') return this.csvRows.rows;
      if (!this.manual.text.trim()) return [];
      return [{ text: this.manual.text.trim(), name: this.manual.name.trim() || null, job_title: this.manual.job_title.trim() || null,
        company: this.manual.company.trim() || null, rating: this.manual.rating || null, date: this.manual.date || null,
        photo_image_id: this.photo ? this.photo.id : null }];
    },
    rowCount() { return this.rows.length; },
    upgradeTarget() { return (this.upgrade && this.upgrade.upgrade_to) || 'solo'; },
    importLabel() { return this.rowCount ? 'Import ' + plural(this.rowCount, 'testimonial') : 'Import'; },
    resultTitle() {
      return this.publishedNow ? 'Published ' + plural(this.publishedNow, 'testimonial') : 'Imported ' + plural(this.result.imported, 'testimonial');
    },
    // After "Publish all" stopped at the plan's limit: what went live and what is waiting.
    resultText() {
      if (this.publishedNow) {
        const waiting = this.result.imported - this.publishedNow;
        return this.publishedNow + ' of the ' + this.result.imported + ' imported testimonials are published. '
          + (waiting === 1 ? 'The other one waits' : 'The other ' + waiting + ' wait') + ' in Ready to publish until your plan has room.';
      }
      return this.result.status === 'approved'
        ? plural(this.result.imported, 'testimonial') + ' imported as approved. Publish them all now, or choose which ones go live.'
        : plural(this.result.imported, 'testimonial') + ' imported for review. They go to your client for approval before publishing.';
    }
  },
  watch: {
    pasteText(value) {
      const previous = this.blocks;
      this.blocks = parseBlocks(value).map((block, index) => {
        const old = previous[index];
        return old && old.text === block.text ? old : block;
      });
    }
  },
  async mounted() { await this.requireWorkspace(); },
  methods: {
    plural,
    withStop,
    reset() {
      this.result = null; this.publishedNow = 0; this.pasteText = ''; this.table = []; this.csvName = ''; this.photo = null; this.tried = false;
      this.manual = { text: '', name: '', job_title: '', company: '', date: '', rating: null };
    },
    pickCsv(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = '';
      this.csvError = '';
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) { this.csvError = 'That file is larger than 5 MB. Split it into smaller files.'; return; }
      const reader = new FileReader();
      reader.onload = () => {
        const table = parseCsv(reader.result);
        if (!table.length) { this.csvError = 'That file has no rows we can read.'; return; }
        this.table = table;
        this.csvName = file.name;
        this.hasHeader = looksLikeHeader(table[0]);
        const mapping = this.hasHeader ? mapHeaders(table[0]) : {};
        Object.keys(FIELD_LABELS).forEach(key => { if (mapping[key] === undefined) mapping[key] = -1; });
        if (mapping.text < 0) mapping.text = 0;
        this.mapping = mapping;
      };
      reader.onerror = () => { this.csvError = 'That file could not be read.'; };
      reader.readAsText(file);
    },
    async pickPhoto(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = '';
      if (!file) return;
      if (['image/png', 'image/jpeg', 'image/webp'].indexOf(file.type) < 0 || file.size > 8 * 1024 * 1024) {
        this.photoStatus = 'That photo couldn\'t be read. Try a JPG or PNG under 8 MB.';
        return;
      }
      this.photoStatus = 'Uploading…';
      const body = new FormData();
      body.append('kind', 'photo');
      body.append('brand_id', String(this.brandId));
      body.append('file', file);
      try {
        this.photo = await uploadForm('/images', body);
        this.photoStatus = file.name + ' · ready';
      } catch (error) { this.photo = null; this.photoStatus = message(error); }
    },
    async submit() {
      this.tried = true;
      this.error = '';
      if (!this.source.trim() || !this.attest || !this.rowCount) return;
      this.busy = true;
      try {
        this.result = await post('/brands/' + this.brandId + '/imports', { rows: this.rows, source_label: this.source.trim(), attest: true,
          origin: this.mode });
        toast('Imported ' + plural(this.result.imported, 'testimonial') + '.');
        this.refreshWorkspace();
      } catch (error) { this.error = message(error); } finally { this.busy = false; }
    },
    async publishAll() {
      this.busy = true;
      try {
        const outcome = await post('/submissions/actions', { ids: this.result.ids, action: 'publish' });
        if (outcome.limit) { this.upgrade = outcome.limit; this.upgradeOpen = true; this.publishedNow = outcome.done.length; }
        if (outcome.done.length) {
          const done = publishedToast(this.brand, outcome.done.length);
          toast(done.text, 'success', done.action);
        }
        this.refreshWorkspace();
        if (!outcome.limit) this.$router.push('/testimonials').catch(() => {});
      } catch (error) {
        const limit = planLimit(error);
        if (limit) { this.upgrade = limit; this.upgradeOpen = true; } else toast(message(error), 'error');
      } finally { this.busy = false; }
    }
  }
};
</script>

<style scoped>
.im>*{max-width:880px}
.im-back{margin-bottom:16px}
.im-tabs{margin-bottom:18px}
.im-h{margin-bottom:16px}
.im-paste{min-height:200px}
.im-blocks{margin-top:18px}
.im-block{padding:12px;border:1px solid #E6DED2;border-radius:12px;margin-bottom:10px;background:#FBF8F3}
.im-block-text{margin:0 0 10px;font:500 15px/1.55 var(--font-display)!important;color:#1F2328;white-space:pre-line}
.im-block-fields{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.im-header{margin:16px 0!important}
.im-map{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(200px,100%),1fr));gap:12px}
.im-map-row .pb-label{margin-bottom:6px}
.im-preview-title{margin:18px 0 8px}
.im-table-wrap{overflow-x:auto;border:1px solid #E6DED2;border-radius:12px}
.im-table{width:100%;border-collapse:collapse;font-size:13.5px}
.im-table th,.im-table td{padding:8px 12px;text-align:left;border-bottom:1px solid #EFE9E0;vertical-align:top}
.im-table th{background:#FBF8F3;color:#4F4A45;font-weight:600}
.im-table td:first-child{min-width:260px;max-width:420px}
.im-problems{margin:6px 0 0;padding-left:18px}
.im-two{display:grid;grid-template-columns:1fr 1fr;gap:0 16px}
.im-confirm{margin-top:18px}
.im-attest{margin-bottom:6px!important}
.im-go{margin-top:18px}
.im-done{text-align:center;padding:40px 28px}
.im-done .pb-body{max-width:520px;margin:10px auto 22px}
.im-done-actions{justify-content:center}
@media(max-width:600px){.im-block-fields,.im-two{grid-template-columns:1fr}.im-go .pb-button{width:100%}}
</style>
